import { Col, Row } from '@opendoor/atoms/box';
import { Combobox } from '@opendoor/atoms/combobox';
import { Field } from '@opendoor/atoms/field';
import { Text } from '@opendoor/atoms/text';
import type { ErrorKey, FilterErrors, RangeOption } from 'components/marketplace/search/types';

import { buildRangeFilterOptions, filterIntOptions } from './utils';

const MIN_PLACEHOLDER: RangeOption = { label: 'No min', value: '0' };
const MAX_PLACEHOLDER: RangeOption = { label: 'No max', value: '10000' };

const SquareFootageOptions: RangeOption[] = [
  { label: '1,000 sq ft', value: '1000' },
  { label: '1,500 sq ft', value: '1500' },
  { label: '2,000 sq ft', value: '2000' },
  { label: '2,500 sq ft', value: '2500' },
  { label: '3,000 sq ft', value: '3000' },
  { label: '3,500 sq ft', value: '3500' },
  { label: '4,000 sq ft', value: '4000' },
  { label: '5,000 sq ft', value: '5000' },
];

export interface SquareFootageFilterProps {
  minSquareFootage?: string | null;
  maxSquareFootage?: string | null;
  handleMinSquareFootageChange: (value: string) => void;
  handleMaxSquareFootageChange: (value: string) => void;
  handleError: (key: ErrorKey, val: string) => void;
  errors?: FilterErrors | null;
}

export const SquareFootageFilter = ({
  minSquareFootage,
  maxSquareFootage,
  handleMinSquareFootageChange,
  handleMaxSquareFootageChange,
  handleError,
  errors,
}: SquareFootageFilterProps) => {
  const onMinSquareFootageChange = (value: string) => {
    if (maxSquareFootage && parseInt(value, 10) >= parseInt(maxSquareFootage, 10)) {
      handleError('minSquareFootage', 'Min square footage must be less than max square footage');
      return;
    }
    handleError('minSquareFootage', '');
    handleMinSquareFootageChange(value);
  };

  const onMaxSquareFootageChange = (value: string) => {
    if (minSquareFootage && parseInt(value, 10) < parseInt(minSquareFootage, 10)) {
      handleError('maxSquareFootage', 'Max square footage must be greater than min square footage');

      return;
    }
    handleError('maxSquareFootage', '');
    handleMaxSquareFootageChange(value);
  };

  const minOptions = buildRangeFilterOptions(
    filterIntOptions(SquareFootageOptions, 'lt', maxSquareFootage ?? undefined),
    MIN_PLACEHOLDER,
  );
  const maxOptions = buildRangeFilterOptions(
    filterIntOptions(SquareFootageOptions, 'gt', minSquareFootage ?? undefined),
    MAX_PLACEHOLDER,
  );

  return (
    <Col className="w-full gap-8">
      <Text as="p" className="text-label-xl">
        Square feet
      </Text>
      <Row className="w-full justify-between">
        <Field className="w-[48%]" error={errors?.['minSquareFootage'] || undefined}>
          <Combobox
            aria-label="Minimum square footage"
            placeholder={MIN_PLACEHOLDER.label}
            options={minOptions}
            value={minOptions.find((option) => option.value === minSquareFootage) ?? null}
            error={Boolean(errors?.['minSquareFootage'])}
            size="sm"
            onValueChange={(option) =>
              onMinSquareFootageChange(option?.value ?? MIN_PLACEHOLDER.value)
            }
          />
        </Field>
        <Field className="w-[48%]" error={errors?.['maxSquareFootage'] || undefined}>
          <Combobox
            aria-label="Maximum square footage"
            placeholder={MAX_PLACEHOLDER.label}
            options={maxOptions}
            value={maxOptions.find((option) => option.value === maxSquareFootage) ?? null}
            error={Boolean(errors?.['maxSquareFootage'])}
            size="sm"
            onValueChange={(option) =>
              onMaxSquareFootageChange(option?.value ?? MAX_PLACEHOLDER.value)
            }
          />
        </Field>
      </Row>
    </Col>
  );
};
